import React, { useState } from "react";
import { Phone, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const STATUS = [
  { v: "new", label: "New", cls: "text-sky-300" },
  { v: "interested", label: "Interested", cls: "text-emerald-300" },
  { v: "follow_up", label: "Follow-up", cls: "text-gold" },
  { v: "not_received", label: "Not received (call again)", cls: "text-orange-300" },
  { v: "approved", label: "Approved for travel", cls: "text-cyan-300" },
  { v: "make_agent", label: "Make Associate Partner", cls: "text-fuchsia-300" },
  { v: "not_interested", label: "Not interested (delete)", cls: "text-red-300" },
];

const TEMPLATES = [
  { k: "offer", label: "Package offer", t: (n) => `Assalamu Alaikum ${n || ""}! Bengal Umrah House Travels has new Umrah packages with limited seats. Shall I share the details and dates with you?` },
  { k: "follow", label: "Follow-up", t: (n) => `Assalamu Alaikum ${n || ""}! Following up on your Umrah plan with Bengal Umrah House Travels. When would be a good time to talk?` },
  { k: "docs", label: "Documents needed", t: (n) => `Assalamu Alaikum ${n || ""}! For your Umrah booking please send: passport (front & back), 2 photos (white background), Aadhaar card. JazakAllah khair — Bengal Umrah House Travels` },
  { k: "job", label: "Job abroad offer", t: (n) => `Assalamu Alaikum ${n || ""}! Bengal Umrah House Travels has new overseas job openings (Saudi, UAE, Qatar). Shall I share the job list, salary and service charge with you?` },
];

const cell = "bg-transparent border border-transparent hover:border-white/30 focus:border-gold/60 focus:bg-black/30 rounded px-2 py-1.5 text-sm text-white font-medium outline-none w-full";
const today = () => new Date().toISOString().slice(0, 10);

export const TONES = {
  interested: "bg-emerald-500/25 border-l-4 border-l-emerald-400",
  follow_up: "bg-sky-500/25 border-l-4 border-l-sky-400",
  called: "bg-yellow-400/20 border-l-4 border-l-yellow-300",
};

export const LIGHT_TONES = {
  interested: "bg-emerald-300/15 border-l-4 border-l-emerald-200",
  follow_up: "bg-sky-300/15 border-l-4 border-l-sky-200",
  called: "bg-yellow-200/15 border-l-4 border-l-yellow-100",
};

const calledToday = (l) => !!l.last_called && new Date(l.last_called).toDateString() === new Date().toDateString();

export function rowTone(l) {
  const t = calledToday(l) ? LIGHT_TONES : TONES;
  if (l.status === "interested") return t.interested;
  if (l.status === "follow_up") return t.follow_up;
  if ((l.call_count || 0) > 0 || l.last_called || (l.status && l.status !== "new")) return t.called;
  return "border-l-4 border-l-transparent";
}

export function LeadRow({ l, groups, selected, onSelect, onSave, onCalled, onDelete, highlight }) {
  const [menu, setMenu] = useState(false);
  const [draft, setDraft] = useState(l);
  const blur = (k) => { if ((draft[k] || "") !== (l[k] || "")) onSave(l.id, { [k]: draft[k] }); };
  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
  const changeNow = (k) => (e) => {
    const v = e.target.value;
    if (k === "status" && v === "not_interested" && !window.confirm(`Mark ${l.name || l.phone} as Not interested? This deletes the number.`)) return;
    if (k === "status" && v === "approved" && !draft.group_id && (l.category || "umrah") === "umrah") toast.info("Now choose the travel group — they'll be added to Customers automatically");
    if (k === "status" && v === "make_agent" && !window.confirm(`Add ${l.name || l.phone} to the Associate Partner list? They can log in with name + mobile.`)) return;
    setDraft({ ...draft, [k]: v });
    onSave(l.id, { [k]: v });
  };
  const wa = (t) => { onCalled(l.id); window.open(`https://wa.me/${l.whatsapp || l.phone}?text=${encodeURIComponent(t.t(l.name))}`, "_blank"); setMenu(false); };
  const st = STATUS.find((s) => s.v === draft.status) || STATUS[0];

  return (
    <tr data-testid={`lead-row-${l.id}`} className={`border-b border-white/10 transition-colors hover:brightness-125 ${highlight ? "ring-2 ring-inset ring-emerald-400 bg-emerald-500/10" : rowTone({ ...l, ...draft })}`} data-tone={rowTone({ ...l, ...draft }).split(" ")[0]}>
      <td className="px-2"><input type="checkbox" checked={selected} onChange={() => onSelect(l.id)} data-testid={`lead-select-${l.id}`} /></td>
      <td className="min-w-[130px]"><input className={cell} value={draft.name || ""} onChange={set("name")} onBlur={() => blur("name")} placeholder="Name" data-testid={`lead-name-${l.id}`} /></td>
      <td className="font-mono text-sm font-semibold text-gold whitespace-nowrap px-2">+{l.phone}</td>
      <td className="min-w-[115px]"><input className={`${cell} font-mono`} value={draft.whatsapp || ""} onChange={set("whatsapp")} onBlur={() => blur("whatsapp")} data-testid={`lead-wa-${l.id}`} /></td>
      <td className="min-w-[90px]"><input className={cell} value={draft.city || ""} onChange={set("city")} onBlur={() => blur("city")} placeholder="City" /></td>
      <td className="min-w-[140px]">
        <select className={`${cell} ${st.cls} font-semibold`} value={draft.status} onChange={changeNow("status")} data-testid={`lead-status-${l.id}`}>
          {STATUS.map((s) => <option key={s.v} value={s.v} className="bg-[#05261D] text-white">{s.label}</option>)}
        </select>
      </td>
      <td className="min-w-[200px]"><input className={cell} value={draft.demand || ""} onChange={set("demand")} onBlur={() => blur("demand")} placeholder="Package, budget, month…" data-testid={`lead-demand-${l.id}`} /></td>
      <td><input type="date" className={cell} value={draft.next_follow_up || ""} onChange={changeNow("next_follow_up")} data-testid={`lead-follow-${l.id}`} /></td>
      <td><input type="date" className={cell} value={draft.travel_date || ""} onChange={changeNow("travel_date")} /></td>
      <td className="min-w-[140px]">
        {(l.category || "umrah") !== "umrah" ? <span className="text-xs text-white/80 px-2">{l.category === "job" ? "Job lead" : "Agent lead"}</span> : (
          <select className={cell} value={draft.group_id || ""} onChange={changeNow("group_id")} data-testid={`lead-group-${l.id}`}>
            <option value="" className="bg-[#05261D]">—</option>
            {groups.map((g) => <option key={g.id} value={g.id} className="bg-[#05261D]">{`${g.name} · ${g.date}${g.package ? ` · ${g.package}` : ""}${g.price ? ` · ₹${Number(g.price).toLocaleString("en-IN")}` : ""}`}</option>)}
          </select>
        )}
        {l.contact_id && <span className="text-[11px] text-cyan-300 px-2">✓ in Customers</span>}
        {l.agent_user_id && <span className="text-[11px] text-fuchsia-300 px-2" data-testid={`lead-agent-done-${l.id}`}>✓ Associate Partner</span>}
      </td>
      <td className="text-center text-sm font-semibold text-white">{l.call_count || 0}</td>
      <td className="text-xs text-white/80 whitespace-nowrap px-1.5" data-testid={`lead-caller-${l.id}`}>
        {l.last_called ? `${new Date(l.last_called).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · ${l.called_by || ""}` : "—"}
        {l.assigned_to && <span className="block text-emerald-300 font-semibold">☎ {l.assigned_to}</span>}
      </td>
      <td className="whitespace-nowrap px-1 relative">
        <a href={`tel:+${l.phone}`} onClick={() => onCalled(l.id)} data-testid={`lead-call-${l.id}`} title="Call from your phone"
          className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/30 mr-1"><Phone className="w-3.5 h-3.5" /></a>
        <button type="button" onClick={() => setMenu(!menu)} data-testid={`lead-wa-btn-${l.id}`} title="WhatsApp from your phone"
          className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366]/30 mr-1"><MessageCircle className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => onDelete(l)} data-testid={`lead-del-${l.id}`} title="Delete"
          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-red-300 hover:bg-red-500/20"><Trash2 className="w-3.5 h-3.5" /></button>
        {menu && (
          <div className="absolute right-2 top-9 z-30 w-48 rounded-lg border border-gold/30 bg-[#05261D] shadow-xl p-1" data-testid={`lead-wa-menu-${l.id}`}>
            {TEMPLATES.map((t) => (
              <button key={t.k} type="button" onClick={() => wa(t)} data-testid={`lead-wa-tpl-${t.k}-${l.id}`}
                className="block w-full text-left text-xs px-2.5 py-2 rounded hover:bg-gold/10 text-white">{t.label}</button>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}
